/**
 * Client Announcements
 * View system announcements
 */

import React, { useState, useEffect } from 'react';
import {
  Megaphone,
  Pin,
  Calendar,
  Eye,
  CheckCircle,
  Filter,
} from 'lucide-react';

interface Announcement {
  id: number;
  title: string;
  content: string;
  category: string | null;
  is_pinned: number;
  published_at: string;
  views: number;
}

export const Announcements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [readAnnouncements, setReadAnnouncements] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadAnnouncements();
    loadUnreadCount();
  }, [filterCategory]);

  const loadAnnouncements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);

      const response = await fetch(`/api/v1/client/announcements?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setAnnouncements(data.data.announcements);
      }
    } catch (error) {
      console.error('Failed to load announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await fetch('/api/v1/client/announcements/unread-count', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.data.count);
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const handleViewAnnouncement = async (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);

    // Mark as read if not already read
    if (!readAnnouncements.has(announcement.id)) {
      try {
        await fetch(`/api/v1/client/announcements/${announcement.id}/mark-read`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        setReadAnnouncements(new Set(readAnnouncements).add(announcement.id));
        loadUnreadCount();
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const getCategoryColor = (category: string | null) => {
    const colors = {
      general: 'from-blue-500 to-blue-600',
      maintenance: 'from-yellow-500 to-yellow-600',
      feature: 'from-green-500 to-green-600',
      security: 'from-red-500 to-red-600',
    };

    return colors[category as keyof typeof colors] || 'from-gray-500 to-gray-600';
  };

  const getCategoryBadge = (category: string | null) => {
    const colors = {
      general: 'bg-blue-900 text-blue-300',
      maintenance: 'bg-yellow-900 text-yellow-300',
      feature: 'bg-green-900 text-green-300',
      security: 'bg-red-900 text-red-300',
    };

    return colors[category as keyof typeof colors] || 'bg-slate-700/50 text-slate-300';
  };

  // If an announcement is selected, show the detail view
  if (selectedAnnouncement) {
    return (
      <div className="p-6">
        {/* Back button */}
        <button
          onClick={() => setSelectedAnnouncement(null)}
          className="text-orange-400 hover:text-orange-300 mb-6 flex items-center gap-2"
        >
          ← Back to Announcements
        </button>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
          {/* Category badge */}
          {selectedAnnouncement.category && (
            <div className="mb-4">
              <span
                className={`px-3 py-1 rounded-full text-sm ${getCategoryBadge(
                  selectedAnnouncement.category
                )}`}
              >
                {selectedAnnouncement.category}
              </span>
            </div>
          )}

          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
            {selectedAnnouncement.is_pinned === 1 && (
              <Pin className="w-6 h-6 text-purple-400" />
            )}
            {selectedAnnouncement.title}
          </h1>

          {/* Meta info */}
          <div className="flex items-center gap-4 text-slate-400 text-sm mb-6 pb-6 border-b border-slate-700/50">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(selectedAnnouncement.published_at).toLocaleString()}
            </div>
            <div className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {selectedAnnouncement.views} views
            </div>
          </div>

          {/* Content */}
          <div
            className="prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: selectedAnnouncement.content }}
          />
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Megaphone className="w-6 h-6" />
              Announcements
            </h1>
            <p className="text-slate-400 mt-1">
              Stay updated with the latest news and updates
            </p>
          </div>
          {unreadCount > 0 && (
            <div className="px-4 py-2 bg-orange-500 text-white rounded-lg">
              {unreadCount} unread
            </div>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-slate-400" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
          >
            <option value="">All Categories</option>
            <option value="general">General</option>
            <option value="maintenance">Maintenance</option>
            <option value="feature">Features</option>
            <option value="security">Security</option>
          </select>
        </div>
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : announcements.length === 0 ? (
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 text-center">
            <Megaphone className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No announcements to display</p>
          </div>
        ) : (
          announcements.map((announcement) => {
            const isRead = readAnnouncements.has(announcement.id);
            const isPinned = announcement.is_pinned === 1;

            return (
              <button
                key={announcement.id}
                onClick={() => handleViewAnnouncement(announcement)}
                className={`w-full bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 text-left hover:bg-slate-700/30 transition-colors border-l-4 ${
                  isPinned
                    ? 'border-purple-500'
                    : isRead
                    ? 'border-slate-700/50'
                    : 'border-blue-500'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {isPinned && <Pin className="w-5 h-5 text-purple-400" />}
                    <h3 className="text-xl font-medium text-white">
                      {announcement.title}
                    </h3>
                  </div>
                  {!isRead && (
                    <span className="px-3 py-1 bg-orange-500 text-white text-xs rounded-full">
                      NEW
                    </span>
                  )}
                  {isRead && (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  )}
                </div>

                <div className="flex items-center gap-4 mb-3">
                  {announcement.category && (
                    <span
                      className={`px-2 py-1 rounded text-xs ${getCategoryBadge(
                        announcement.category
                      )}`}
                    >
                      {announcement.category}
                    </span>
                  )}
                  <span className="text-slate-400 text-sm flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(announcement.published_at).toLocaleDateString()}
                  </span>
                  <span className="text-slate-400 text-sm flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {announcement.views} views
                  </span>
                </div>

                <div className="text-slate-400 line-clamp-2">
                  {announcement.content.replace(/<[^>]*>/g, '')}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Announcements;
